import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { ChevronDown, Info, Eye, EyeOff } from 'lucide-react';
import { Modal, FormActionButtons } from '../../../components/ui';
import { FormField, Input, Select, Textarea } from '../../../components/ui/FormComponents';

import { useEmployeeForm } from '../hooks/useEmployeeForm';
import { Toast } from '../../../components/ui/Toast';

const CustomRoleSelect = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  /*developer: Nithin
      date : 2024-06-19
      changes : Replaced Telecaller With Staff  and commented not required roles */

  const options = [
    { value: "admin", label: "Admin" },
    { value: "accountant", label: "Accountant" },
    { value: 'inventory_manager', label: "Inventory Manager" },
    { value: 'store_incharge', label: "Store Incharge" },
    { value: 'store_staff', label: "Store Staff" },
  ];

  const selectedLabel = options.find(o => o.value === value)?.label || "Select Role";

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        className="w-full h-[36px] px-3 text-[12px] border border-slate-200 rounded-md text-[13px] text-slate-800 bg-white cursor-pointer flex items-center justify-between shadow-sm focus:outline-none focus:border-slate-800 transition-all"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={value ? "text-slate-800" : "text-slate-400"}>{selectedLabel}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <div className="absolute z-[100] top-[calc(100%+4px)] left-0 w-full bg-white border border-slate-200 shadow-xl rounded-md overflow-y-auto max-h-[210px] animate-in fade-in zoom-in-95 duration-100">
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`px-3 py-2 text-[12px] cursor-pointer hover:bg-slate-50 transition-colors ${value === opt.value ? 'bg-slate-50 font-bold text-slate-900' : 'text-slate-700'}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const roleDescriptions = [
  { label: "Admin", color: "#6366f1", desc: "Full access to all modules, settings and team management" },
  { label: "Accountant", color: "#f59e0b", desc: "Manages transactions, invoices and financial records" },
  { label: "Inventory Manager", color: "#8b5cf6", desc: "Manages overall inventory across multiple branches" },
  { label: "Store Incharge", color: "#10b981", desc: "Manages store inventory, stock and material requests" },
  { label: "Store Staff", color: "#06b6d4", desc: "Assists with store operations and inventory" },
];

const RoleInfoPopover = () => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative inline-flex items-center" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="ml-1.5 w-[15px] h-[15px] rounded-full bg-slate-200 hover:bg-slate-800 text-slate-500 hover:text-white flex items-center justify-center transition-all duration-200 focus:outline-none"
        title="View role descriptions"
      >
        <Info className="w-[9px] h-[9px]" />
      </button>

      {open && (
        <div className="absolute left-6 top-0 z-[200] w-[270px] bg-white border border-slate-200 rounded-xl shadow-2xl p-3 animate-in fade-in slide-in-from-left-2 duration-150">
          <div className="flex items-center gap-1.5 mb-2.5 pb-2 border-b border-slate-100">
            <Info className="w-3 h-3 text-slate-400" />
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Role Descriptions</span>
          </div>
          <div className="flex flex-col gap-2">
            {roleDescriptions.map((r) => (
              <div key={r.label} className="flex items-start gap-2">
                <span
                  className="mt-[3px] w-[7px] h-[7px] rounded-full flex-shrink-0"
                  style={{ backgroundColor: r.color }}
                />
                <div>
                  <span className="text-[11px] font-bold text-slate-800">{r.label}</span>
                  <p className="text-[10px] text-slate-500 leading-tight mt-0.5">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const AddEmployeeModal = ({ isOpen, onClose, onSubmit, isLoading, initialData }) => {
  const {
    register,
    submit,
    errors,
    role,
    locations,
    isEdit,
    setValue,
    control
  } = useEmployeeForm({ isOpen, initialData, onSubmit });

  const [toast, setToast] = React.useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const todayDate = new Date().toISOString().split('T')[0];

  const onInvalid = (formErrors) => {
    const first = Object.values(formErrors || {})?.[0];
    const msg = first?.message || 'Please fix the highlighted fields.';
    setToast({ message: msg, type: 'error' });
  };

  // Shared classes for elegant high-contrast elements inside white modal
  const inputClass = "text-[12px] font-medium text-slate-800 bg-white border-slate-200 focus:border-slate-800 transition-all h-[36px] shadow-sm rounded-md";
  const fieldLabel = (text, req = false) => (
    <span className="text-slate-700 font-medium text-[12px] tracking-wide">
      {text} {req && <span className="text-red-500 ml-0.5">*</span>}
    </span>
  );

  return (
    <>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <div className="flex flex-col gap-0.5 z-10 relative">
            <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
              SPACE DESIGNS
            </span>
            <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
              {isEdit ? 'Modify Employee / Contractor Profile' : 'Employee / Contractor Onboarding Panel'}
            </span>
          </div>
        }
        size="lg"
        footer={
          <FormActionButtons 
            onClose={onClose}
            cancelText="Cancel"
            submitText={isEdit ? 'Save Changes' : 'Create Member'}
            formId="add-employee-form"
            isLoading={isLoading}
            containerClassName="flex flex-col-reverse sm:flex-row items-center w-full gap-3"
          />
        }
      >
        {/* Form fields directly laid out on a clean white background */}
        <form id="add-employee-form" onSubmit={submit(onInvalid)} className="flex flex-col gap-4">
          {/* Row 1: First Name & Last Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label={fieldLabel('First Name', true)} error={errors.firstName} field="firstName">
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <Input
                    id="firstName"
                    className={inputClass}
                    placeholder="First name"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      const val = e.target.value.toLowerCase().replace(/\s+/g, '');
                      if (val) {
                        setValue('email', `${val}@spacedesigns.com`, { shouldValidate: true });
                      } else {
                        setValue('email', '');
                      }
                    }}
                  />
                )}
              />
            </FormField>

            <FormField label={fieldLabel('Last Name')} error={errors.lastName} field="lastName">
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <Input id="lastName" className={inputClass} placeholder="Last name" {...field} />
                )}
              />
            </FormField>
          </div>

          {/* Row 2: Phone & Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label={fieldLabel('Phone Number', true)} error={errors.phone} field="phone">
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <Input id="phone" className={inputClass} placeholder="10 digit mobile" {...field} maxLength={10} inputMode="numeric" />
                )}
              />
            </FormField>

            <FormField label={fieldLabel('Email Address', true)} error={errors.email} field="email">
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input id="email" className={inputClass} placeholder="email@spacedesigns.com" type="email" {...field} />
                )}
              />
            </FormField>
          </div>

          {/* Row 3: Employee ID & Role */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label={fieldLabel('Employee ID', true)} error={errors.employeeId} field="employeeId">
              <Controller
                name="employeeId"
                control={control}
                render={({ field }) => (
                  <Input
                    id="employeeId"
                    className={inputClass}
                    placeholder="EMP001"
                    {...field}
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                  />
                )}
              />
            </FormField>

            <FormField
              label={
                <span className="flex items-center">
                  {fieldLabel('Role Assignment', true)}
                  {/* <RoleInfoPopover /> */}
                </span>
              }
              error={errors.role}
              field="role"
            >
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <CustomRoleSelect value={field.value} onChange={field.onChange} />
                )}
              />
            </FormField>
          </div>

          {/* Row 4: Joining Date, Store Location & Password */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField label={fieldLabel('Joining Date', true)} error={errors.joiningDate} field="joiningDate">
              <Controller
                name="joiningDate"
                control={control}
                render={({ field }) => (
                  <Input id="joiningDate" className={`${inputClass} cursor-pointer`} type="date" max={todayDate} {...field} />
                )}
              />
            </FormField>

            <FormField label={fieldLabel('Store Location', true)} error={errors.workLocation} field="workLocation">
              <Controller
                name="workLocation"
                control={control}
                render={({ field }) => (
                  <Select id="workLocation" className={`${inputClass} pr-10`} {...field}>
                    <option value="">Select location</option>
                    {locations.map((loc) => (
                      <option key={loc._id} value={loc.name}>
                        {loc.name}
                      </option>
                    ))}
                  </Select>
                )}
              />
            </FormField>

            <FormField
              label={fieldLabel(isEdit ? 'New Password' : 'Password', !isEdit)}
              error={errors.password}
              field="password"
            >
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <Input
                      id="password"
                      className={`${inputClass} pr-10`}
                      placeholder={
                        isEdit
                          ? 'Leave blank'
                          : '8–10 chars: 1 uppercase'
                      }
                      type={showPassword ? "text" : "password"}
                      maxLength={10}
                      {...field}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                    >
                      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                )}
              />
            </FormField>
          </div>

          {/* Notes */}
          <FormField label={fieldLabel('Administrative Notes')} error={errors.notes}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <Textarea className="text-[12px] font-medium text-slate-800 bg-white border-slate-200 focus:border-slate-800 transition-all font-medium text-[12px] shadow-sm rounded-md" placeholder="Background info or notes..." {...field} rows={2} />
              )}
            />
          </FormField>
        </form>
      </Modal>
    </>
  );
};




